import { transformError as sdkTransformError } from '@wix/sdk-runtime/transform-error';
import { queryBuilder } from '@wix/sdk-runtime/query-builder';
import {
  renameKeysFromSDKRequestToRESTRequest,
  renameKeysFromRESTResponseToSDKResponse,
} from '@wix/sdk-runtime/rename-all-nested-keys';
import {
  HttpClient,
  HttpResponse,
  NonNullablePaths,
  QuerySpec,
  Query as QuerySdkType,
} from '@wix/sdk-types';
import * as ambassadorWixBillingV1ManualTaxMapping from './billing-v1-manual-tax-mapping-manual-tax-mappings.http.js';
// @ts-ignore
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { createQueryUtils } from '@wix/sdk-runtime/query-builder-utils';

export interface ManualTaxMapping {
  /**
   * Manual tax mapping ID.
   * @format GUID
   * @readonly
   */
  _id?: string | null;
  /**
   * Revision number, which increments by 1 each time the manual tax mapping is updated.
   * To prevent conflicting changes, the current revision must be passed when updating the manual tax mapping.
   *
   * Ignored when creating a manual tax mapping.
   * @readonly
   */
  revision?: string | null;
  /**
   * Date and time the manual tax mapping was created.
   * @readonly
   */
  _createdDate?: Date | null;
  /**
   * Date and time the manual tax mapping was last updated.
   * @readonly
   */
  _updatedDate?: Date | null;
  /**
   * Tax group ID. Tax groups define collections of items or services that share the same tax treatment.
   * @format GUID
   */
  taxGroupId?: string | null;
  /**
   * Tax region ID. Tax regions represent geographical areas where specific tax rules apply.
   * @format GUID
   */
  taxRegionId?: string | null;
  /**
   * Tax rate to apply for this tax group and region combination.
   *
   * Specified as a decimal string representing a fraction, not a percentage. For example, `"0.05"` is 5%, `"0.1"` is 10%, and `"0.08"` is 8%. Don't pass a number (`0.05`), an integer (`5`), or a percentage string (`"5%"`). Up to 6 decimal places are supported.
   * @decimalValue options { maxScale:6, gte:0 }
   */
  taxRate?: string;
  /**
   * Description of the manual tax mapping. Use this field to add context or notes about the tax rate.
   * @maxLength 1000
   */
  description?: string | null;
  /**
   * Type of tax being applied, such as `VAT`, `GST`, or `Sales Tax`. This depends on the jurisdiction's tax laws.
   * @maxLength 200
   */
  taxType?: string;
  /**
   * Customer-facing name of the tax, shown at checkout. For example, `STATE TAX`, `GST`, or `VAT`.
   *
   * Cosmetic only. It doesn't affect the calculated amount. Can be left empty, in which case checkout shows a default label such as `Sales Tax`.
   * @maxLength 200
   */
  taxName?: string;
  /**
   * Name of the jurisdiction where this tax rate applies. For example, `California` or `United Kingdom`.
   * @maxLength 200
   */
  jurisdiction?: string;
  /** Type of jurisdiction where this tax rate applies, such as country, state, county, city, or special district. */
  jurisdictionType?: JurisdictionTypeWithLiterals;
  /**
   * Tax exemption details for user-defined exempt groups.
   * @maxSize 50
   */
  exemptRates?: ExemptDetails[];
  /** Extended fields for storing additional custom data. */
  extendedFields?: ExtendedFields;
}

/** Type of jurisdiction that taxes were calculated for. For example, `"State"`, `"County"`, `"City"`, `"Special"`, etc. */
export enum JurisdictionType {
  UNDEFINED = 'UNDEFINED',
  COUNTRY = 'COUNTRY',
  STATE = 'STATE',
  COUNTY = 'COUNTY',
  CITY = 'CITY',
  SPECIAL = 'SPECIAL',
}

/** @enumType */
export type JurisdictionTypeWithLiterals =
  | JurisdictionType
  | 'UNDEFINED'
  | 'COUNTRY'
  | 'STATE'
  | 'COUNTY'
  | 'CITY'
  | 'SPECIAL';

export interface ExemptDetails {
  /**
   * Tax exempt group ID.
   * @format GUID
   */
  taxExemptGroupId?: string;
  /**
   * Tax override rate for given tax mapping.
   * @decimalValue options { maxScale:6, gte:0 }
   */
  taxRate?: string;
}

export interface ExtendedFields {
  /**
   * Extended field data. Each key corresponds to the namespace of the app that created the extended fields.
   * The value of each key is structured according to the schema defined when the extended fields were configured.
   *
   * You can only access fields for which you have the appropriate permissions.
   *
   * Learn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).
   */
  namespaces?: Record<string, Record<string, any>>;
}

export interface CreateManualTaxMappingRequest {
  /** Manual tax mapping to create. */
  manualTaxMapping: ManualTaxMapping;
}

export interface CreateManualTaxMappingResponse {
  /** Created manual tax mapping. */
  manualTaxMapping?: ManualTaxMapping;
}

export interface BulkCreateManualTaxMappingsRequest {
  /**
   * Manual tax mappings to create.
   * @minSize 1
   * @maxSize 100
   */
  manualTaxMappings: ManualTaxMapping[];
  /**
   * Whether to return the created manual tax mapping entities in the response.
   *
   * Default: `false`
   */
  returnEntity?: boolean;
}

export interface BulkCreateManualTaxMappingsResponse {
  /**
   * Results of the bulk create operation, including created manual tax mappings and metadata.
   * @minSize 1
   * @maxSize 100
   */
  results?: BulkManualTaxMappingResult[];
  /** Metadata for the bulk create operation. */
  bulkActionMetadata?: BulkActionMetadata;
}

export interface ItemMetadata {
  /**
   * Item ID. Provided only whenever possible. For example, `itemId` can't be provided when item creation has failed.
   * @format GUID
   */
  _id?: string | null;
  /** Index of the item within the request array. Allows for correlation between request and response items. */
  originalIndex?: number;
  /** Whether the requested action for this item was successful. When `false`, the `error` field is returned. */
  success?: boolean;
  /** Details about the error in case of failure. */
  error?: ApplicationError;
}

export interface ApplicationError {
  /** Error code. */
  code?: string;
  /** Description of the error. */
  description?: string;
  /** Data related to the error. */
  data?: Record<string, any> | null;
}

export interface BulkManualTaxMappingResult {
  /** Metadata for the individual create operation. */
  itemMetadata?: ItemMetadata;
  /** Created manual tax mapping. Only present if `returnEntity` was set to `true` in the request. */
  item?: ManualTaxMapping;
}

export interface BulkActionMetadata {
  /** Number of items that were successfully processed. */
  totalSuccesses?: number;
  /** Number of items that couldn't be processed. */
  totalFailures?: number;
  /** Number of failures without details because detailed failure threshold was exceeded. */
  undetailedFailures?: number;
}

export interface GetManualTaxMappingRequest {
  /**
   * Manual tax mapping ID.
   * @format GUID
   */
  manualTaxMappingId: string;
}

export interface GetManualTaxMappingResponse {
  /** Retrieved manual tax mapping. */
  manualTaxMapping?: ManualTaxMapping;
}

export interface UpdateManualTaxMappingRequest {
  /** Manual tax mapping to update. */
  manualTaxMapping: ManualTaxMapping;
  /**
   * Set of fields to update.
   *
   * Fields that aren't included in `fieldMask.paths` are ignored.
   * @internal
   */
  fieldMask?: string[];
}

export interface UpdateManualTaxMappingResponse {
  /** Updated manual tax mapping. */
  manualTaxMapping?: ManualTaxMapping;
}

export interface DeleteManualTaxMappingRequest {
  /**
   * Manual tax mapping ID.
   * @format GUID
   */
  manualTaxMappingId: string;
}

export interface DeleteManualTaxMappingResponse {}

export interface QueryManualTaxMappingsRequest {
  /** Query options. */
  query?: CursorQuery;
}

export interface CursorQuery extends CursorQueryPagingMethodOneOf {
  /**
   * Cursor paging options.
   *
   * Learn more about [cursor paging](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language#cursor-paging).
   */
  cursorPaging?: CursorPaging;
  /**
   * Filter object.
   *
   * Learn more about the [filter section](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language#the-filter-section).
   */
  filter?: Record<string, any> | null;
  /**
   * Sort object.
   *
   * Learn more about the [sort section](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language#the-sort-section).
   * @maxSize 5
   */
  sort?: Sorting[];
}

/** @oneof */
export interface CursorQueryPagingMethodOneOf {
  /**
   * Cursor paging options.
   *
   * Learn more about [cursor paging](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language#cursor-paging).
   */
  cursorPaging?: CursorPaging;
}

export interface Sorting {
  /**
   * Name of the field to sort by.
   * @maxLength 50
   */
  fieldName?: string;
  /** Sort order. */
  order?: SortOrderWithLiterals;
}

export enum SortOrder {
  ASC = 'ASC',
  DESC = 'DESC',
}

/** @enumType */
export type SortOrderWithLiterals = SortOrder | 'ASC' | 'DESC';

export interface CursorPaging {
  /**
   * Maximum number of items to return in the results.
   * @max 100
   */
  limit?: number | null;
  /**
   * Pointer to the next or previous page in the list of results.
   *
   * Pass the relevant cursor token from the `pagingMetadata` object in the previous call's response.
   * Not relevant for the first request.
   * @maxLength 16000
   */
  cursor?: string | null;
}

export interface QueryManualTaxMappingsResponse {
  /**
   * Manual tax mappings that match the query criteria.
   * @maxSize 100
   */
  manualTaxMappings?: ManualTaxMapping[];
  /** Pagination metadata. */
  pagingMetadata?: CursorPagingMetadata;
}

export interface CursorPagingMetadata {
  /** Number of items returned in current page. */
  count?: number | null;
  /** Cursor strings that point to the next page, previous page, or both. */
  cursors?: Cursors;
  /**
   * Whether there are more pages to retrieve following the current page.
   *
   * + `true`: Another page of results can be retrieved.
   * + `false`: This is the last page.
   */
  hasNext?: boolean | null;
}

export interface Cursors {
  /**
   * Cursor string pointing to the next page in the list of results.
   * @maxLength 16000
   */
  next?: string | null;
  /**
   * Cursor pointing to the previous page in the list of results.
   * @maxLength 16000
   */
  prev?: string | null;
}

export interface UpdateExtendedFieldsRequest {
  /** ID of the entity to update. */
  _id: string;
  /** Identifier for the app whose extended fields are being updated. */
  namespace: string;
  /** Data to update. Structured according to the [schema](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields#json-schema-for-extended-fields) defined when the extended fields were configured. */
  namespaceData: Record<string, any> | null;
}

export interface UpdateExtendedFieldsResponse {
  /** Updated manual tax mapping. */
  manualTaxMapping?: ManualTaxMapping;
}

export interface BulkRestoreManualTaxMappingsRequest {
  /**
   * Manual tax mapping IDs to restore.
   * @format GUID
   * @minSize 1
   * @maxSize 200
   */
  manualTaxMappingIds?: string[];
}

export interface BulkRestoreManualTaxMappingsResponse {
  /**
   * Restored manual tax mappings
   * @minSize 1
   * @maxSize 200
   */
  results?: BulkRestoreManualTaxMappingsResult[];
}

export interface BulkRestoreManualTaxMappingsResult {
  itemMetadata?: ItemMetadata;
}

export interface BulkAssignExemptOverridesRequest {
  /**
   * List of assign overrides.
   * @maxSize 50
   */
  assignOverrides?: AssignOverride[];
  /**
   * Whether to return the updated manual tax mapping entities in the response.
   * Default: `false`
   */
  returnEntity?: boolean;
}

export interface AssignOverride {
  /**
   * ID of the manual tax mapping to which the exempt group override will be assigned.
   * @format GUID
   */
  manualTaxMappingId?: string;
  /**
   * List of exempt group overrides to assign to the manual tax mapping.
   * @minSize 1
   * @maxSize 50
   */
  exemptGroupOverrides?: ExemptGroupIdOverride[];
}

export interface ExemptGroupIdOverride {
  /**
   * ID of the tax exempt group.
   * @format GUID
   */
  exemptGroupId?: string;
  /**
   * Override tax rate for the exempt group in the context of the manual tax mapping.
   * @format DECIMAL_VALUE
   * @decimalValue options { maxScale:3 }
   */
  rate?: string;
}

export interface BulkAssignExemptOverridesResponse {
  /**
   * List of updated manual tax mappings.
   * @maxSize 50
   */
  results?: BulkExemptOverrideResult[];
  /** Metadata for the bulk assign operation. */
  bulkActionMetadata?: BulkActionMetadata;
}

export interface BulkExemptOverrideResult {
  /** Metadata for the individual assign operation. */
  itemMetadata?: ItemMetadata;
  /** Updated manual tax mapping. Only present if `returnEntity` was set to `true` in the request. */
  item?: ManualTaxMapping;
}

export interface BulkUnassignExemptOverridesRequest {
  /**
   * List of unassign overrides.
   * @maxSize 50
   */
  unassignOverrides: UnassignOverride[];
  /**
   * Whether to return the updated manual tax mapping entities in the response.
   * Default: `false`
   */
  returnEntity?: boolean;
}

export interface UnassignOverride {
  /**
   * ID of the manual tax mapping from which the exempt group override will be unassigned.
   * @format GUID
   */
  manualTaxMappingId?: string;
  /**
   * List of exempt group IDs to unassign from the manual tax mapping.
   * @format GUID
   * @minSize 1
   * @maxSize 50
   */
  exemptGroupIds?: string[];
}

export interface BulkUnassignExemptOverridesResponse {
  /**
   * List of updated manual tax mappings.
   * @maxSize 10
   */
  results?: BulkExemptOverrideResult[];
  /** Metadata for the bulk assign operation. */
  bulkActionMetadata?: BulkActionMetadata;
}

export interface ListManualTaxMappingsRequest {}

export interface ListManualTaxMappingsResponse {
  /**
   * List of manual tax mappings.
   * @maxSize 1000
   */
  manualTaxMappings?: ManualTaxMapping[];
  /** Pagination metadata. */
  pagingMetadata?: CursorPagingMetadata;
}

export interface DomainEvent extends DomainEventBodyOneOf {
  createdEvent?: EntityCreatedEvent;
  updatedEvent?: EntityUpdatedEvent;
  deletedEvent?: EntityDeletedEvent;
  actionEvent?: ActionEvent;
  /** Event ID. With this ID you can easily spot duplicated events and ignore them. */
  _id?: string;
  /**
   * Fully Qualified Domain Name of an entity. This is a unique identifier assigned to the API main business entities.
   * For example, `wix.stores.catalog.product`, `wix.bookings.session`, `wix.payments.transaction`.
   */
  entityFqdn?: string;
  /**
   * Event action name, placed at the top level to make it easier for users to dispatch messages.
   * For example: `created`/`updated`/`deleted`/`started`/`completed`/`email_opened`.
   */
  slug?: string;
  /** ID of the entity associated with the event. */
  entityId?: string;
  /** Event timestamp in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601) format and UTC time. For example, `2020-04-26T13:57:50.699Z`. */
  eventTime?: Date | null;
  /**
   * Whether the event was triggered as a result of a privacy regulation application
   * (for example, GDPR).
   */
  triggeredByAnonymizeRequest?: boolean | null;
  /** If present, indicates the action that triggered the event. */
  originatedFrom?: string | null;
  /**
   * A sequence number that indicates the order of updates to an entity. For example, if an entity was updated at `16:00` and then again at `16:01`, the second update will always have a higher sequence number.
   * You can use this number to make sure you're handling updates in the right order. Just save the latest sequence number on your end and compare it to the one in each new message. If the new message has an older (lower) number, you can safely ignore it.
   */
  entityEventSequence?: string | null;
}

/** @oneof */
export interface DomainEventBodyOneOf {
  createdEvent?: EntityCreatedEvent;
  updatedEvent?: EntityUpdatedEvent;
  deletedEvent?: EntityDeletedEvent;
  actionEvent?: ActionEvent;
}

export interface EntityCreatedEvent {
  entity?: string;
}

export interface RestoreInfo {
  deletedDate?: Date | null;
}

export interface EntityUpdatedEvent {
  /**
   * Since platformized APIs only expose PATCH and not PUT we can't assume that the fields sent from the client are the actual diff.
   * This means that to generate a list of changed fields (as opposed to sent fields) one needs to traverse both objects.
   * We don't want to impose this on all developers and so we leave this traversal to the notification recipients which need it.
   */
  currentEntity?: string;
}

export interface EntityDeletedEvent {
  /** Entity that was deleted. */
  deletedEntity?: string | null;
}

export interface ActionEvent {
  body?: string;
}

export interface Empty {}

export interface MessageEnvelope {
  /**
   * App instance ID.
   * @format GUID
   */
  instanceId?: string | null;
  /**
   * Event type.
   * @maxLength 150
   */
  eventType?: string;
  /** The identification type and identity data. */
  identity?: IdentificationData;
  /** Stringify payload. */
  data?: string;
  /** Details related to the account */
  accountInfo?: AccountInfo;
}

export interface IdentificationData extends IdentificationDataIdOneOf {
  /**
   * ID of a site visitor that has not logged in to the site.
   * @format GUID
   */
  anonymousVisitorId?: string;
  /**
   * ID of a site visitor that has logged in to the site.
   * @format GUID
   */
  memberId?: string;
  /**
   * ID of a Wix user (site owner, contributor, etc.).
   * @format GUID
   */
  wixUserId?: string;
  /**
   * ID of an app.
   * @format GUID
   */
  appId?: string;
  /** @readonly */
  identityType?: WebhookIdentityTypeWithLiterals;
}

/** @oneof */
export interface IdentificationDataIdOneOf {
  /**
   * ID of a site visitor that has not logged in to the site.
   * @format GUID
   */
  anonymousVisitorId?: string;
  /**
   * ID of a site visitor that has logged in to the site.
   * @format GUID
   */
  memberId?: string;
  /**
   * ID of a Wix user (site owner, contributor, etc.).
   * @format GUID
   */
  wixUserId?: string;
  /**
   * ID of an app.
   * @format GUID
   */
  appId?: string;
}

export enum WebhookIdentityType {
  UNKNOWN = 'UNKNOWN',
  ANONYMOUS_VISITOR = 'ANONYMOUS_VISITOR',
  MEMBER = 'MEMBER',
  WIX_USER = 'WIX_USER',
  APP = 'APP',
}

/** @enumType */
export type WebhookIdentityTypeWithLiterals =
  | WebhookIdentityType
  | 'UNKNOWN'
  | 'ANONYMOUS_VISITOR'
  | 'MEMBER'
  | 'WIX_USER'
  | 'APP';

export interface AccountInfo {
  /**
   * ID of the Wix account associated with the event.
   * @format GUID
   */
  accountId?: string | null;
  /**
   * ID of the parent Wix account. Only included when accountId belongs to a child account.
   * @format GUID
   */
  parentAccountId?: string | null;
  /**
   * ID of the Wix site associated with the event. Only included when the event is tied to a specific site.
   * @format GUID
   */
  siteId?: string | null;
}

/** @docsIgnore */
export type CreateManualTaxMappingApplicationErrors =
  | {
      code?: 'TAX_REGION_ID_NOT_FOUND';
      description?: string;
      data?: Record<string, any>;
    }
  | {
      code?: 'TAX_GROUP_ID_NOT_FOUND';
      description?: string;
      data?: Record<string, any>;
    }
  | {
      code?: 'MANUAL_TAX_MAPPING_ALREADY_EXISTS';
      description?: string;
      data?: Record<string, any>;
    };
/** @docsIgnore */
export type UpdateManualTaxMappingApplicationErrors =
  | {
      code?: 'MANUAL_TAX_MAPPING_ALREADY_EXISTS';
      description?: string;
      data?: Record<string, any>;
    }
  | {
      code?: 'TAX_REGION_ID_NOT_FOUND';
      description?: string;
      data?: Record<string, any>;
    }
  | {
      code?: 'TAX_GROUP_ID_NOT_FOUND';
      description?: string;
      data?: Record<string, any>;
    };

export interface BaseEventMetadata {
  /**
   * App instance ID.
   * @format GUID
   */
  instanceId?: string | null;
  /**
   * Event type.
   * @maxLength 150
   */
  eventType?: string;
  /** The identification type and identity data. */
  identity?: IdentificationData;
  /** Details related to the account */
  accountInfo?: AccountInfo;
}

export interface EventMetadata extends BaseEventMetadata {
  /** Event ID. With this ID you can easily spot duplicated events and ignore them. */
  _id?: string;
  /**
   * Fully Qualified Domain Name of an entity. This is a unique identifier assigned to the API main business entities.
   * For example, `wix.stores.catalog.product`, `wix.bookings.session`, `wix.payments.transaction`.
   */
  entityFqdn?: string;
  /**
   * Event action name, placed at the top level to make it easier for users to dispatch messages.
   * For example: `created`/`updated`/`deleted`/`started`/`completed`/`email_opened`.
   */
  slug?: string;
  /** ID of the entity associated with the event. */
  entityId?: string;
  /** Event timestamp in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601) format and UTC time. For example, `2020-04-26T13:57:50.699Z`. */
  eventTime?: Date | null;
  /**
   * Whether the event was triggered as a result of a privacy regulation application
   * (for example, GDPR).
   */
  triggeredByAnonymizeRequest?: boolean | null;
  /** If present, indicates the action that triggered the event. */
  originatedFrom?: string | null;
  /**
   * A sequence number that indicates the order of updates to an entity. For example, if an entity was updated at `16:00` and then again at `16:01`, the second update will always have a higher sequence number.
   * You can use this number to make sure you're handling updates in the right order. Just save the latest sequence number on your end and compare it to the one in each new message. If the new message has an older (lower) number, you can safely ignore it.
   */
  entityEventSequence?: string | null;
  accountInfo?: AccountInfoMetadata;
}

export interface AccountInfoMetadata {
  /** ID of the Wix account associated with the event */
  accountId: string;
  /** ID of the Wix site associated with the event. Only included when the event is tied to a specific site. */
  siteId?: string;
  /** ID of the parent Wix account. Only included when 'accountId' belongs to a child account. */
  parentAccountId?: string;
}

export interface ManualTaxMappingCreatedEnvelope {
  entity: ManualTaxMapping;
  metadata: EventMetadata;
}

/**
 * Triggered when a manual tax mapping is created.
 * @permissionScope Manage Stores - all permissions
 * @permissionScopeId SCOPE.DC-STORES-MEGA.MANAGE-STORES
 * @permissionScope Manage_Wix_Rentals_App_Forms_submissions
 * @permissionScopeId SCOPE.RENTALS.MANAGE
 * @permissionScope Manage Stores
 * @permissionScopeId SCOPE.STORES.MANAGE-STORES
 * @permissionScope Manage Events
 * @permissionScopeId SCOPE.EVENTS.MANAGE-EVENTS
 * @permissionScope Manage Invoices
 * @permissionScopeId SCOPE.INVOICES.MANAGE
 * @permissionScope Manage Restaurants - all permissions
 * @permissionScopeId SCOPE.RESTAURANTS.MEGA-SCOPES
 * @permissionScope Manage eCommerce - all permissions
 * @permissionScopeId SCOPE.DC-ECOM-MEGA.MANAGE-ECOM
 * @permissionScope Manage Orders
 * @permissionScopeId SCOPE.DC-STORES.MANAGE-ORDERS
 * @permissionId BILLING.MANUAL_TAX_MAPPING_READ
 * @webhook
 * @eventType wix.billing.v1.manual_tax_mapping_created
 * @slug created
 */
export declare function onManualTaxMappingCreated(
  handler: (event: ManualTaxMappingCreatedEnvelope) => void | Promise<void>
): void;

export interface ManualTaxMappingDeletedEnvelope {
  entity: ManualTaxMapping;
  metadata: EventMetadata;
}

/**
 * Triggered when a manual tax mapping is deleted.
 * @permissionScope Manage Stores - all permissions
 * @permissionScopeId SCOPE.DC-STORES-MEGA.MANAGE-STORES
 * @permissionScope Manage_Wix_Rentals_App_Forms_submissions
 * @permissionScopeId SCOPE.RENTALS.MANAGE
 * @permissionScope Manage Stores
 * @permissionScopeId SCOPE.STORES.MANAGE-STORES
 * @permissionScope Manage Events
 * @permissionScopeId SCOPE.EVENTS.MANAGE-EVENTS
 * @permissionScope Manage Invoices
 * @permissionScopeId SCOPE.INVOICES.MANAGE
 * @permissionScope Manage Restaurants - all permissions
 * @permissionScopeId SCOPE.RESTAURANTS.MEGA-SCOPES
 * @permissionScope Manage eCommerce - all permissions
 * @permissionScopeId SCOPE.DC-ECOM-MEGA.MANAGE-ECOM
 * @permissionScope Manage Orders
 * @permissionScopeId SCOPE.DC-STORES.MANAGE-ORDERS
 * @permissionId BILLING.MANUAL_TAX_MAPPING_READ
 * @webhook
 * @eventType wix.billing.v1.manual_tax_mapping_deleted
 * @slug deleted
 */
export declare function onManualTaxMappingDeleted(
  handler: (event: ManualTaxMappingDeletedEnvelope) => void | Promise<void>
): void;

export interface ManualTaxMappingUpdatedEnvelope {
  entity: ManualTaxMapping;
  metadata: EventMetadata;
}

/**
 * Triggered when a manual tax mapping is updated.
 * @permissionScope Manage Stores - all permissions
 * @permissionScopeId SCOPE.DC-STORES-MEGA.MANAGE-STORES
 * @permissionScope Manage_Wix_Rentals_App_Forms_submissions
 * @permissionScopeId SCOPE.RENTALS.MANAGE
 * @permissionScope Manage Stores
 * @permissionScopeId SCOPE.STORES.MANAGE-STORES
 * @permissionScope Manage Events
 * @permissionScopeId SCOPE.EVENTS.MANAGE-EVENTS
 * @permissionScope Manage Invoices
 * @permissionScopeId SCOPE.INVOICES.MANAGE
 * @permissionScope Manage Restaurants - all permissions
 * @permissionScopeId SCOPE.RESTAURANTS.MEGA-SCOPES
 * @permissionScope Manage eCommerce - all permissions
 * @permissionScopeId SCOPE.DC-ECOM-MEGA.MANAGE-ECOM
 * @permissionScope Manage Orders
 * @permissionScopeId SCOPE.DC-STORES.MANAGE-ORDERS
 * @permissionId BILLING.MANUAL_TAX_MAPPING_READ
 * @webhook
 * @eventType wix.billing.v1.manual_tax_mapping_updated
 * @slug updated
 */
export declare function onManualTaxMappingUpdated(
  handler: (event: ManualTaxMappingUpdatedEnvelope) => void | Promise<void>
): void;

/**
 * Creates a manual tax mapping for a specific tax group and region combination.
 *
 * You must specify the tax group ID and tax region ID. The tax group and tax region
 * must already exist before creating the mapping.
 * To create multiple manual tax mappings in a single request, use [Bulk Create Manual Tax Mappings](https://dev.wix.com/docs/api-reference/business-solutions/e-commerce/extensions/tax/manual-tax-mappings/bulk-create-manual-tax-mappings).
 * @param manualTaxMapping - Manual tax mapping to create.
 * @public
 * @requiredField manualTaxMapping
 * @requiredField manualTaxMapping.taxGroupId
 * @requiredField manualTaxMapping.taxRate
 * @requiredField manualTaxMapping.taxRegionId
 * @permissionId BILLING.MANUAL_TAX_MAPPING_CREATE
 * @applicableIdentity APP
 * @returns Created manual tax mapping.
 * @fqn wix.billing.v1.ManualTaxMappingService.CreateManualTaxMapping
 */
export async function createManualTaxMapping(
  manualTaxMapping: NonNullablePaths<
    ManualTaxMapping,
    `taxGroupId` | `taxRate` | `taxRegionId`,
    2
  >
): Promise<
  NonNullablePaths<
    ManualTaxMapping,
    | `taxRate`
    | `taxType`
    | `taxName`
    | `jurisdiction`
    | `jurisdictionType`
    | `exemptRates`
    | `exemptRates.${number}.taxExemptGroupId`
    | `exemptRates.${number}.taxRate`,
    4
  > & {
    __applicationErrorsType?: CreateManualTaxMappingApplicationErrors;
  }
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    manualTaxMapping: manualTaxMapping,
  });

  const reqOpts =
    ambassadorWixBillingV1ManualTaxMapping.createManualTaxMapping(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)
      ?.manualTaxMapping!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { manualTaxMapping: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['manualTaxMapping']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

/**
 * Creates up to 100 manual tax mappings in a single request.
 *
 * Use this method when you need to set up tax mappings for multiple tax group and region combinations at once,
 * such as when initially configuring tax rates for a new business or importing mappings from an external system.
 * @param manualTaxMappings - Manual tax mappings to create.
 * @public
 * @requiredField manualTaxMappings
 * @requiredField manualTaxMappings.taxGroupId
 * @requiredField manualTaxMappings.taxRate
 * @requiredField manualTaxMappings.taxRegionId
 * @permissionId BILLING.MANUAL_TAX_MAPPING_CREATE
 * @applicableIdentity APP
 * @fqn wix.billing.v1.ManualTaxMappingService.BulkCreateManualTaxMappings
 */
export async function bulkCreateManualTaxMappings(
  manualTaxMappings: NonNullablePaths<
    ManualTaxMapping,
    `taxGroupId` | `taxRate` | `taxRegionId`,
    2
  >[],
  options?: BulkCreateManualTaxMappingsOptions
): Promise<
  NonNullablePaths<
    BulkCreateManualTaxMappingsResponse,
    | `results`
    | `results.${number}.itemMetadata.originalIndex`
    | `results.${number}.itemMetadata.success`
    | `results.${number}.itemMetadata.error.code`
    | `results.${number}.itemMetadata.error.description`
    | `results.${number}.item.taxRate`
    | `results.${number}.item.taxType`
    | `results.${number}.item.taxName`
    | `results.${number}.item.jurisdiction`
    | `results.${number}.item.jurisdictionType`
    | `bulkActionMetadata.totalSuccesses`
    | `bulkActionMetadata.totalFailures`
    | `bulkActionMetadata.undetailedFailures`,
    6
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    manualTaxMappings: manualTaxMappings,
    returnEntity: options?.returnEntity,
  });

  const reqOpts =
    ambassadorWixBillingV1ManualTaxMapping.bulkCreateManualTaxMappings(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          manualTaxMappings: '$[0]',
          returnEntity: '$[1].returnEntity',
        },
        singleArgumentUnchanged: false,
      },
      ['manualTaxMappings', 'options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface BulkCreateManualTaxMappingsOptions {
  /**
   * Whether to return the created manual tax mapping entities in the response.
   *
   * Default: `false`
   */
  returnEntity?: boolean;
}

/**
 * Retrieves a manual tax mapping by ID.
 *
 * Use this method to get details about a specific manual tax mapping, including its tax rate,
 * jurisdiction information, and associated tax group and region.
 * @param manualTaxMappingId - Manual tax mapping ID.
 * @public
 * @requiredField manualTaxMappingId
 * @permissionId BILLING.MANUAL_TAX_MAPPING_READ
 * @applicableIdentity APP
 * @returns Retrieved manual tax mapping.
 * @fqn wix.billing.v1.ManualTaxMappingService.GetManualTaxMapping
 */
export async function getManualTaxMapping(
  manualTaxMappingId: string
): Promise<
  NonNullablePaths<
    ManualTaxMapping,
    | `taxRate`
    | `taxType`
    | `taxName`
    | `jurisdiction`
    | `jurisdictionType`
    | `exemptRates`
    | `exemptRates.${number}.taxExemptGroupId`
    | `exemptRates.${number}.taxRate`,
    4
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    manualTaxMappingId: manualTaxMappingId,
  });

  const reqOpts =
    ambassadorWixBillingV1ManualTaxMapping.getManualTaxMapping(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)
      ?.manualTaxMapping!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { manualTaxMappingId: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['manualTaxMappingId']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

/**
 * Updates an existing manual tax mapping.
 *
 * Use this method to modify the tax rate or other details of an existing manual tax mapping.
 * Each time the manual tax mapping is updated, `revision` increments by 1.
 * The current `revision` must be passed when updating the manual tax mapping.
 * This ensures you're working with the latest manual tax mapping and prevents unintended overwrites.
 * @param _id - Manual tax mapping ID.
 * @public
 * @requiredField _id
 * @requiredField manualTaxMapping
 * @requiredField manualTaxMapping.revision
 * @permissionId BILLING.MANUAL_TAX_MAPPING_UPDATE
 * @applicableIdentity APP
 * @returns Updated manual tax mapping.
 * @fqn wix.billing.v1.ManualTaxMappingService.UpdateManualTaxMapping
 */
export async function updateManualTaxMapping(
  _id: string,
  manualTaxMapping: NonNullablePaths<UpdateManualTaxMapping, `revision`, 2>
): Promise<
  NonNullablePaths<
    ManualTaxMapping,
    | `taxRate`
    | `taxType`
    | `taxName`
    | `jurisdiction`
    | `jurisdictionType`
    | `exemptRates`
    | `exemptRates.${number}.taxExemptGroupId`
    | `exemptRates.${number}.taxRate`,
    4
  > & {
    __applicationErrorsType?: UpdateManualTaxMappingApplicationErrors;
  }
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    manualTaxMapping: { ...manualTaxMapping, id: _id },
  });

  const reqOpts =
    ambassadorWixBillingV1ManualTaxMapping.updateManualTaxMapping(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)
      ?.manualTaxMapping!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: { manualTaxMapping: '$[1]' },
        explicitPathsToArguments: { 'manualTaxMapping.id': '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['_id', 'manualTaxMapping']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface UpdateManualTaxMapping {
  /**
   * Manual tax mapping ID.
   * @format GUID
   * @readonly
   */
  _id?: string | null;
  /**
   * Revision number, which increments by 1 each time the manual tax mapping is updated.
   * To prevent conflicting changes, the current revision must be passed when updating the manual tax mapping.
   *
   * Ignored when creating a manual tax mapping.
   * @readonly
   */
  revision?: string | null;
  /**
   * Date and time the manual tax mapping was created.
   * @readonly
   */
  _createdDate?: Date | null;
  /**
   * Date and time the manual tax mapping was last updated.
   * @readonly
   */
  _updatedDate?: Date | null;
  /**
   * Tax group ID. Tax groups define collections of items or services that share the same tax treatment.
   * @format GUID
   */
  taxGroupId?: string | null;
  /**
   * Tax region ID. Tax regions represent geographical areas where specific tax rules apply.
   * @format GUID
   */
  taxRegionId?: string | null;
  /**
   * Tax rate to apply for this tax group and region combination.
   *
   * Specified as a decimal string representing a fraction, not a percentage. For example, `"0.05"` is 5%, `"0.1"` is 10%, and `"0.08"` is 8%. Don't pass a number (`0.05`), an integer (`5`), or a percentage string (`"5%"`). Up to 6 decimal places are supported.
   * @decimalValue options { maxScale:6, gte:0 }
   */
  taxRate?: string;
  /**
   * Description of the manual tax mapping. Use this field to add context or notes about the tax rate.
   * @maxLength 1000
   */
  description?: string | null;
  /**
   * Type of tax being applied, such as `VAT`, `GST`, or `Sales Tax`. This depends on the jurisdiction's tax laws.
   * @maxLength 200
   */
  taxType?: string;
  /**
   * Customer-facing name of the tax, shown at checkout. For example, `STATE TAX`, `GST`, or `VAT`.
   *
   * Cosmetic only. It doesn't affect the calculated amount. Can be left empty, in which case checkout shows a default label such as `Sales Tax`.
   * @maxLength 200
   */
  taxName?: string;
  /**
   * Name of the jurisdiction where this tax rate applies. For example, `California` or `United Kingdom`.
   * @maxLength 200
   */
  jurisdiction?: string;
  /** Type of jurisdiction where this tax rate applies, such as country, state, county, city, or special district. */
  jurisdictionType?: JurisdictionTypeWithLiterals;
  /**
   * Tax exemption details for user-defined exempt groups.
   * @maxSize 50
   */
  exemptRates?: ExemptDetails[];
  /** Extended fields for storing additional custom data. */
  extendedFields?: ExtendedFields;
}

/**
 * Deletes a manual tax mapping.
 *
 * This permanently removes the custom tax rate mapping.
 * After deletion, no tax will be calculated for the associated tax group and region combination.
 * @param manualTaxMappingId - Manual tax mapping ID.
 * @public
 * @requiredField manualTaxMappingId
 * @permissionId BILLING.MANUAL_TAX_MAPPING_DELETE
 * @applicableIdentity APP
 * @fqn wix.billing.v1.ManualTaxMappingService.DeleteManualTaxMapping
 */
export async function deleteManualTaxMapping(
  manualTaxMappingId: string
): Promise<void> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    manualTaxMappingId: manualTaxMappingId,
  });

  const reqOpts =
    ambassadorWixBillingV1ManualTaxMapping.deleteManualTaxMapping(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { manualTaxMappingId: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['manualTaxMappingId']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

/**
 * Retrieves a list of manual tax mappings based on the provided query criteria.
 *
 * Use this method to find manual tax mappings by tax group, tax region, or other filter criteria.
 * You can also sort and paginate the results. This is useful for managing large numbers of tax mappings
 * or finding mappings that apply to specific regions or tax groups.
 *
 * To learn how to query manual tax mappings, see [API Query Language](https://dev.wix.com/docs/api-reference/articles/work-with-wix-apis/data-retrieval/about-the-wix-api-query-language).
 * @public
 * @permissionId BILLING.MANUAL_TAX_MAPPING_READ
 * @applicableIdentity APP
 * @fqn wix.billing.v1.ManualTaxMappingService.QueryManualTaxMappings
 */
export function queryManualTaxMappings(): ManualTaxMappingsQueryBuilder {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[0] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  return queryBuilder<
    ManualTaxMapping,
    'CURSOR',
    QueryManualTaxMappingsRequest,
    QueryManualTaxMappingsResponse
  >({
    func: async (payload: QueryManualTaxMappingsRequest) => {
      const reqOpts =
        ambassadorWixBillingV1ManualTaxMapping.queryManualTaxMappings(payload);

      sideEffects?.onSiteCall?.();
      try {
        const result = await httpClient.request(reqOpts);
        sideEffects?.onSuccess?.(result);
        return result;
      } catch (err) {
        sideEffects?.onError?.(err);
        throw err;
      }
    },
    requestTransformer: (query: QueryManualTaxMappingsRequest['query']) => {
      const args = [query, {}] as [QueryManualTaxMappingsRequest['query'], {}];
      return renameKeysFromSDKRequestToRESTRequest({
        ...args?.[1],
        query: args?.[0],
      });
    },
    responseTransformer: ({
      data,
    }: HttpResponse<QueryManualTaxMappingsResponse>) => {
      const transformedData = renameKeysFromRESTResponseToSDKResponse(
        transformPaths(data, [])
      );

      return {
        items: transformedData?.manualTaxMappings,
        pagingMetadata: transformedData?.pagingMetadata,
      };
    },
    errorTransformer: (err: unknown) => {
      const transformedError = sdkTransformError(err, {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { query: '$[0]' },
        singleArgumentUnchanged: false,
      });

      throw transformedError;
    },
    pagingMethod: 'CURSOR',
    transformationPaths: {},
  });
}

interface QueryCursorResult {
  cursors: Cursors;
  hasNext: () => boolean;
  hasPrev: () => boolean;
  length: number;
  pageSize: number;
}

export interface ManualTaxMappingsQueryResult extends QueryCursorResult {
  items: ManualTaxMapping[];
  query: ManualTaxMappingsQueryBuilder;
  next: () => Promise<ManualTaxMappingsQueryResult>;
  prev: () => Promise<ManualTaxMappingsQueryResult>;
}

export interface ManualTaxMappingsQueryBuilder {
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   */
  eq: (
    propertyName:
      | '_id'
      | '_createdDate'
      | '_updatedDate'
      | 'taxGroupId'
      | 'taxRegionId'
      | 'taxRate'
      | 'description'
      | 'taxType'
      | 'taxName'
      | 'jurisdiction'
      | 'jurisdictionType'
      | 'exemptRates.taxExemptGroupId',
    value: any
  ) => ManualTaxMappingsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   */
  ne: (
    propertyName:
      | '_id'
      | '_createdDate'
      | '_updatedDate'
      | 'taxGroupId'
      | 'taxRegionId'
      | 'taxRate'
      | 'description'
      | 'taxType'
      | 'taxName'
      | 'jurisdiction'
      | 'jurisdictionType'
      | 'exemptRates.taxExemptGroupId',
    value: any
  ) => ManualTaxMappingsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   */
  ge: (
    propertyName:
      | '_id'
      | '_createdDate'
      | '_updatedDate'
      | 'taxGroupId'
      | 'taxRegionId'
      | 'taxRate'
      | 'description'
      | 'taxType'
      | 'taxName'
      | 'jurisdiction'
      | 'exemptRates.taxExemptGroupId',
    value: any
  ) => ManualTaxMappingsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   */
  gt: (
    propertyName:
      | '_id'
      | '_createdDate'
      | '_updatedDate'
      | 'taxGroupId'
      | 'taxRegionId'
      | 'taxRate'
      | 'description'
      | 'taxType'
      | 'taxName'
      | 'jurisdiction'
      | 'exemptRates.taxExemptGroupId',
    value: any
  ) => ManualTaxMappingsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   */
  le: (
    propertyName:
      | '_id'
      | '_createdDate'
      | '_updatedDate'
      | 'taxGroupId'
      | 'taxRegionId'
      | 'taxRate'
      | 'description'
      | 'taxType'
      | 'taxName'
      | 'jurisdiction'
      | 'exemptRates.taxExemptGroupId',
    value: any
  ) => ManualTaxMappingsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   */
  lt: (
    propertyName:
      | '_id'
      | '_createdDate'
      | '_updatedDate'
      | 'taxGroupId'
      | 'taxRegionId'
      | 'taxRate'
      | 'description'
      | 'taxType'
      | 'taxName'
      | 'jurisdiction'
      | 'exemptRates.taxExemptGroupId',
    value: any
  ) => ManualTaxMappingsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `string`.
   * @param string - String to compare against. Case-insensitive.
   */
  startsWith: (
    propertyName:
      | '_id'
      | 'taxGroupId'
      | 'taxRegionId'
      | 'taxRate'
      | 'description'
      | 'taxType'
      | 'taxName'
      | 'jurisdiction'
      | 'exemptRates.taxExemptGroupId',
    value: string
  ) => ManualTaxMappingsQueryBuilder;
  in: (
    propertyName:
      | '_id'
      | '_createdDate'
      | '_updatedDate'
      | 'taxGroupId'
      | 'taxRegionId'
      | 'taxRate'
      | 'description'
      | 'taxType'
      | 'taxName'
      | 'jurisdiction'
      | 'jurisdictionType'
      | 'exemptRates.taxExemptGroupId',
    value: any
  ) => ManualTaxMappingsQueryBuilder;
  exists: (
    propertyName:
      | '_id'
      | '_createdDate'
      | '_updatedDate'
      | 'taxGroupId'
      | 'taxRegionId'
      | 'taxRate'
      | 'description'
      | 'taxType'
      | 'taxName'
      | 'jurisdiction'
      | 'jurisdictionType'
      | 'exemptRates.taxExemptGroupId',
    value: boolean
  ) => ManualTaxMappingsQueryBuilder;
  /** @param propertyNames - Properties used in the sort. To sort by multiple properties, pass properties as additional arguments. */
  ascending: (
    ...propertyNames: Array<
      | '_id'
      | '_createdDate'
      | '_updatedDate'
      | 'taxRate'
      | 'taxType'
      | 'taxName'
      | 'jurisdiction'
      | 'jurisdictionType'
    >
  ) => ManualTaxMappingsQueryBuilder;
  /** @param propertyNames - Properties used in the sort. To sort by multiple properties, pass properties as additional arguments. */
  descending: (
    ...propertyNames: Array<
      | '_id'
      | '_createdDate'
      | '_updatedDate'
      | 'taxRate'
      | 'taxType'
      | 'taxName'
      | 'jurisdiction'
      | 'jurisdictionType'
    >
  ) => ManualTaxMappingsQueryBuilder;
  /** @param limit - Number of items to return, which is also the `pageSize` of the results object. */
  limit: (limit: number) => ManualTaxMappingsQueryBuilder;
  /** @param cursor - A pointer to specific record */
  skipTo: (cursor: string) => ManualTaxMappingsQueryBuilder;
  find: () => Promise<ManualTaxMappingsQueryResult>;
}

/**
 * @hidden
 * @fqn wix.billing.v1.ManualTaxMappingService.QueryManualTaxMappings
 * @requiredField query
 */
export async function typedQueryManualTaxMappings(
  query: ManualTaxMappingQuery
): Promise<
  NonNullablePaths<
    QueryManualTaxMappingsResponse,
    | `manualTaxMappings`
    | `manualTaxMappings.${number}.taxRate`
    | `manualTaxMappings.${number}.taxType`
    | `manualTaxMappings.${number}.taxName`
    | `manualTaxMappings.${number}.jurisdiction`
    | `manualTaxMappings.${number}.jurisdictionType`,
    4
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({ query: query });

  const reqOpts =
    ambassadorWixBillingV1ManualTaxMapping.queryManualTaxMappings(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { query: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['query']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface ManualTaxMappingQuerySpec extends QuerySpec {
  paging: 'cursor';
  wql: [
    {
      fields: [
        '_createdDate',
        '_id',
        '_updatedDate',
        'jurisdiction',
        'jurisdictionType',
        'taxName',
        'taxRate',
        'taxType'
      ];
      operators: '*';
      sort: 'BOTH';
    },
    {
      fields: ['exemptRates.taxExemptGroupId'];
      operators: ['$hasAll', '$hasSome'];
      sort: 'NONE';
    },
    {
      fields: ['description', 'taxGroupId', 'taxRegionId'];
      operators: '*';
      sort: 'NONE';
    }
  ];
}

export type CommonQueryWithEntityContext = QuerySdkType<
  ManualTaxMapping,
  ManualTaxMappingQuerySpec
>;
export type ManualTaxMappingQuery = {
  /** 
  Cursor paging options.

  Learn more about [cursor paging](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language#cursor-paging).  
  */
  cursorPaging?: {
    /** 
  Maximum number of items to return in the results. 
  @max: 100 
  */
    limit?:
      | NonNullable<CommonQueryWithEntityContext['cursorPaging']>['limit']
      | null;
    /** 
  Pointer to the next or previous page in the list of results.

  Pass the relevant cursor token from the `pagingMetadata` object in the previous call's response.
  Not relevant for the first request. 
  @maxLength: 16000 
  */
    cursor?:
      | NonNullable<CommonQueryWithEntityContext['cursorPaging']>['cursor']
      | null;
  };
  /** 
  Filter object.

  Learn more about the [filter section](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language#the-filter-section).  
  */
  filter?: CommonQueryWithEntityContext['filter'] | null;
  /** 
  Sort object.

  Learn more about the [sort section](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language#the-sort-section). 
  @maxSize: 5 
  */
  sort?: {
    /** 
  Name of the field to sort by. 
  @maxLength: 50 
  */
    fieldName?: NonNullable<
      CommonQueryWithEntityContext['sort']
    >[number]['fieldName'];
    /** 
  Sort order.  
  */
    order?: NonNullable<CommonQueryWithEntityContext['sort']>[number]['order'];
  }[];
};

export const utils = {
  query: /*#__PURE__*/ createQueryUtils<
    ManualTaxMapping,
    ManualTaxMappingQuerySpec,
    ManualTaxMappingQuery
  >(),
};

/**
 * Updates extended fields of a manual tax mapping.
 *
 * Use this method to modify extended field data associated with a manual tax mapping.
 * Unlike the standard update operation, this does not increment the revision number.
 * @param _id - ID of the entity to update.
 * @param namespace - Identifier for the app whose extended fields are being updated.
 * @public
 * @requiredField _id
 * @requiredField namespace
 * @requiredField options
 * @requiredField options.namespaceData
 * @permissionId BILLING.MANUAL_TAX_MAPPING_UPDATE
 * @applicableIdentity APP
 * @fqn wix.billing.v1.ManualTaxMappingService.UpdateExtendedFields
 */
export async function updateExtendedFields(
  _id: string,
  namespace: string,
  options: NonNullablePaths<UpdateExtendedFieldsOptions, `namespaceData`, 2>
): Promise<
  NonNullablePaths<
    UpdateExtendedFieldsResponse,
    | `manualTaxMapping.taxRate`
    | `manualTaxMapping.taxType`
    | `manualTaxMapping.taxName`
    | `manualTaxMapping.jurisdiction`
    | `manualTaxMapping.jurisdictionType`
    | `manualTaxMapping.exemptRates`
    | `manualTaxMapping.exemptRates.${number}.taxExemptGroupId`
    | `manualTaxMapping.exemptRates.${number}.taxRate`,
    5
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[3] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    id: _id,
    namespace: namespace,
    namespaceData: options?.namespaceData,
  });

  const reqOpts =
    ambassadorWixBillingV1ManualTaxMapping.updateExtendedFields(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          id: '$[0]',
          namespace: '$[1]',
          namespaceData: '$[2].namespaceData',
        },
        singleArgumentUnchanged: false,
      },
      ['_id', 'namespace', 'options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface UpdateExtendedFieldsOptions {
  /** Data to update. Structured according to the [schema](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields#json-schema-for-extended-fields) defined when the extended fields were configured. */
  namespaceData: Record<string, any> | null;
}

/**
 * Assigns tax exempt overrides to multiple manual tax mappings in a single request.
 * @public
 * @requiredField options.assignOverrides.exemptGroupOverrides
 * @requiredField options.assignOverrides.exemptGroupOverrides.exemptGroupId
 * @requiredField options.assignOverrides.exemptGroupOverrides.rate
 * @requiredField options.assignOverrides.manualTaxMappingId
 * @permissionId BILLING.MANUAL_TAX_MAPPING_UPDATE
 * @applicableIdentity APP
 * @fqn wix.billing.v1.ManualTaxMappingService.BulkAssignExemptOverrides
 */
export async function bulkAssignExemptOverrides(
  options?: NonNullablePaths<
    BulkAssignExemptOverridesOptions,
    | `assignOverrides.${number}.exemptGroupOverrides`
    | `assignOverrides.${number}.exemptGroupOverrides.${number}.exemptGroupId`
    | `assignOverrides.${number}.exemptGroupOverrides.${number}.rate`
    | `assignOverrides.${number}.manualTaxMappingId`,
    6
  >
): Promise<
  NonNullablePaths<
    BulkAssignExemptOverridesResponse,
    | `results`
    | `results.${number}.itemMetadata.originalIndex`
    | `results.${number}.itemMetadata.success`
    | `results.${number}.itemMetadata.error.code`
    | `results.${number}.itemMetadata.error.description`
    | `results.${number}.item.taxRate`
    | `results.${number}.item.taxType`
    | `results.${number}.item.taxName`
    | `results.${number}.item.jurisdiction`
    | `results.${number}.item.jurisdictionType`
    | `bulkActionMetadata.totalSuccesses`
    | `bulkActionMetadata.totalFailures`
    | `bulkActionMetadata.undetailedFailures`,
    6
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    assignOverrides: options?.assignOverrides,
    returnEntity: options?.returnEntity,
  });

  const reqOpts =
    ambassadorWixBillingV1ManualTaxMapping.bulkAssignExemptOverrides(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          assignOverrides: '$[0].assignOverrides',
          returnEntity: '$[0].returnEntity',
        },
        singleArgumentUnchanged: false,
      },
      ['options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface BulkAssignExemptOverridesOptions {
  /**
   * List of assign overrides.
   * @maxSize 50
   */
  assignOverrides?: AssignOverride[];
  /**
   * Whether to return the updated manual tax mapping entities in the response.
   * Default: `false`
   */
  returnEntity?: boolean;
}

/**
 * Unassigns tax exempt overrides to multiple manual tax mappings in a single request.
 * @param unassignOverrides - List of unassign overrides.
 * @public
 * @requiredField unassignOverrides
 * @requiredField unassignOverrides.manualTaxMappingId
 * @permissionId BILLING.MANUAL_TAX_MAPPING_UPDATE
 * @applicableIdentity APP
 * @fqn wix.billing.v1.ManualTaxMappingService.BulkUnassignExemptOverrides
 */
export async function bulkUnassignExemptOverrides(
  unassignOverrides: NonNullablePaths<
    UnassignOverride,
    `manualTaxMappingId`,
    2
  >[],
  options?: BulkUnassignExemptOverridesOptions
): Promise<
  NonNullablePaths<
    BulkUnassignExemptOverridesResponse,
    | `results`
    | `results.${number}.itemMetadata.originalIndex`
    | `results.${number}.itemMetadata.success`
    | `results.${number}.itemMetadata.error.code`
    | `results.${number}.itemMetadata.error.description`
    | `results.${number}.item.taxRate`
    | `results.${number}.item.taxType`
    | `results.${number}.item.taxName`
    | `results.${number}.item.jurisdiction`
    | `results.${number}.item.jurisdictionType`
    | `bulkActionMetadata.totalSuccesses`
    | `bulkActionMetadata.totalFailures`
    | `bulkActionMetadata.undetailedFailures`,
    6
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    unassignOverrides: unassignOverrides,
    returnEntity: options?.returnEntity,
  });

  const reqOpts =
    ambassadorWixBillingV1ManualTaxMapping.bulkUnassignExemptOverrides(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          unassignOverrides: '$[0]',
          returnEntity: '$[1].returnEntity',
        },
        singleArgumentUnchanged: false,
      },
      ['unassignOverrides', 'options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface BulkUnassignExemptOverridesOptions {
  /**
   * Whether to return the updated manual tax mapping entities in the response.
   * Default: `false`
   */
  returnEntity?: boolean;
}

/**
 * Retrieves up to 1000 manual tax mappings.
 * The returned manual tax mappings are sorted by the `createdDate` field in ascending order.
 * @public
 * @permissionId BILLING.MANUAL_TAX_MAPPING_READ
 * @applicableIdentity APP
 * @fqn wix.billing.v1.ManualTaxMappingService.ListManualTaxMappings
 */
export async function listManualTaxMappings(): Promise<
  NonNullablePaths<
    ListManualTaxMappingsResponse,
    | `manualTaxMappings`
    | `manualTaxMappings.${number}.taxRate`
    | `manualTaxMappings.${number}.taxType`
    | `manualTaxMappings.${number}.taxName`
    | `manualTaxMappings.${number}.jurisdiction`
    | `manualTaxMappings.${number}.jurisdictionType`,
    4
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[0] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({});

  const reqOpts =
    ambassadorWixBillingV1ManualTaxMapping.listManualTaxMappings(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {},
        singleArgumentUnchanged: false,
      },
      []
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}
